import { action, computed, observable } from 'mobx';

import { Variables, AppManager, Input } from 'core';
import Alert from 'react-s-alert';
import { ProjectApp, ProjectModel } from 'apps/project/app';
import {
  ProjectCheckQuery,
  ProjectMutation,
  ProjectVisibilityMutation,
  FeaturedProjectMutation,
  TransferProjectMutation,
} from 'apps/project/gql';
import { ConfirmAction } from 'apps/modals/confirmAction';

import { FormStore } from 'libs/forms/app';
import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';

import { toLocalStorage } from 'core/localStorage';

import { conf } from './config.yaml';

export class ProjectSettingsAppManager extends AppManager {
  static registryName = 'ProjectSettingsAppManager';
  @observable isFeatured = false;
  @observable isPrivate = false;
  @observable targetSpace = '@';

  constructor(parent, init = {}) {
    super(parent, init);
    this.AppRef('projectApp', ProjectApp);
    this.checkQuery = new ProjectCheckQuery(this, {
      queryKey: 'checkProject',
      onSysErr: this.onError,
      variables: new Variables({
        space: () =>
          this.params.get('profile') || this.params.get('initiative'),
        slug: () => this.params.get('slug'),
      }),
    });
    this.checkQuery.process(
      result => !!result && ProjectModel.Load(result),
      this.UpdateProject,
    );
    this.checkQuery.query();

    this.projectInput = new Input({
      id: () => this.projectApp.id,
      deleted: () => this.projectApp.deleted,
      // transfered: () => false,
      // switchPublicPrivate: () => false
    });

    // Create the formStore for each field on the UI
    this.featuredProjectForm = new FormStore(this, conf, 'setFeaturedForm');
    this.projectVisibilityForm = new FormStore(this, conf, 'setVisibilityForm');
    this.transferForm = new FormStore(this, conf, 'transferForm');
  }

  @action UpdateProject = project => {
    if (project) {
      this.projectApp = project;

      // Update the local information
      if (project.featuredIn != null && project.featuredIn.length > 0) {
        this.isFeatured = true;
      } else {
        this.isFeatured = false;
      }

      this.isPrivate = project.private;
    }
  };

  onDeleteSuccess = () => {
    const { navigate } = clientStore.routerStore;

    navigate('workspace.spaceProjects', {
      spacePath: clientStore.activeSpace?.path,
    });

    clientStore.UpdateProfileOrTeamWorkspace();

    Alert.success(
      'Your project and its files are being deleted - this may take a few minutes to complete.',
    );
    this.projectApp?.SetPerformingDelete(false);
  };

  onDeleteErr = () => {
    Alert.error(
      'Sorry we failed to delete your Project. Please try again later.',
    );
    this.projectApp?.SetPerformingDelete(false);
  };

  ConfirmDelete = () => {
    const { projectApp } = this;
    if (!projectApp) return;
    projectApp.SetPerformingDelete(true);
    projectApp.SetDeleted(true);

    toLocalStorage('deletingProject', { id: this.projectApp.id }, true);

    this.mutations.push(
      ProjectMutation.New(this.projectInput, {
        onSuccess: this.onDeleteSuccess,
        onSysErr: this.onDeleteErr,
      }),
    );
  };

  BeginDelete = () => {
    this.projectApp.SetDeleting(true);
  };

  CancelDelete = () => {
    this.projectApp.SetDeleting(false);
  };

  @computed get loading() {
    return this.queries.get('checkProject')?.loading;
  }

  @computed get canUpdate() {
    return this.projectApp && this.projectApp.canUpdate;
  }

  @computed get canDelete() {
    return this.projectApp && this.projectApp.canDelete;
  }

  @computed get notFound() {
    return this.checkQuery.notFound;
  }

  // If the user clicks the confirm button of the form, show a confirmation popup
  @action confirmToggleFeatured = () => {
    let comp_title = '';
    let comp_text = '';

    if (this.isFeatured === true) {
      comp_title = 'mark this project as featured?';
      comp_text = 'The project will appear on the landing page';
    } else {
      comp_title = 'set this project as not featured?';
      comp_text = 'The project will not appear on the landing page';
    }

    // The popup will appear as a model component
    modalStore.SetModalComponent(ConfirmAction, {
      callBack: () => this.onConfirmToggleFeatured(),
      title: comp_title,
      undo: true,
      text: comp_text,
      actionName: 'Confirm',
    });
  };

  @action confirmToggleVisibility = () => {
    let comp_title = '';
    let comp_text = '';

    if (this.isPrivate === true) {
      comp_title = 'change this project visibility to private?';
      comp_text =
        'From now on, only the owner of the space will be able to access it';
    } else {
      comp_title = 'make this project public?';
      comp_text = 'It will appear as visible for anyone';
    }

    modalStore.SetModalComponent(ConfirmAction, {
      callBack: () => this.onConfirmToggleVisibility(),
      title: comp_title,
      undo: true,
      text: comp_text,
      actionName: 'Confirm',
    });
  };

  @action confirmTransferProject = options => {
    if (this.targetSpace.length > 0) {
      const comp_title = `transfer the proyect to ${this.targetSpace}?`;
      const comp_text = `${this.targetSpace} will own the project`;

      modalStore.SetModalComponent(ConfirmAction, {
        callBack: () => this.onConfirmTransfer(options),
        title: comp_title,
        undo: true,
        text: comp_text,
        actionName: 'Confirm',
      });
    }
  };

  // Once the user has confirmed the action, the next step consists
  // of executing the graphql mutation
  onConfirmToggleFeatured() {
    let featuredProjectInput;

    // Create the input taking into account if the project will be set as featured or not
    if (this.isFeatured === true) {
      featuredProjectInput = new Input({
        id: this.projectApp.id,
        addFeatured: 'featured_projects', // maybe use here this.isFeatured
      });
    } else {
      featuredProjectInput = new Input({
        id: this.projectApp.id,
        removeFeatured: 'featured_projects',
      });
    }

    // Info: Following the example from src/apps/context/appManager.js
    // Define the actions for the right result of the action and the error
    const opts = {
      onSuccess: result => {
        this.onFeaturedProjectSuccess(result);
      },
      onSysErr: this.onFeaturedProjectError,
      onUserErr: this.onUserError,
    };

    // Finally, push the mutation to the queue passing the input, so it will be executed
    this.mutations.push(
      FeaturedProjectMutation.New(featuredProjectInput, opts),
    );
  }

  onConfirmToggleVisibility() {
    const { route } = clientStore.routerStore;

    const spaceType = () => {
      if (route?.path?.startsWith('/@')) return '@';
      if (route?.path?.startsWith('/+')) return '+';
      return '';
    };

    const projectVisibilityInput = new Input({
      private: this.isPrivate,
      slug: this.projectApp.slug,
      space: `${spaceType()}${this.projectApp.space.content.slug}`,
    });

    const opts = {
      onSuccess: result => this.onToggleProjectVisibilitySuccess(result),
      onSysErr: this.onToggleProjectVisibilityError,
      onUserErr: this.onUserError,
    };

    this.mutations.push(
      ProjectVisibilityMutation.New(projectVisibilityInput, opts),
    );
  }

  onConfirmTransfer() {
    const transferProjectInput = new Input({
      slug: this.projectApp.slug,
      space: this.projectApp.spacePath,
      targetSpace: this.targetSpace,
    });

    const opts = {
      onSuccess: result => this.onTransferProjectSuccess(result),
      onSysErr: this.onTransferProjectError,
      onUserErr: this.onUserError,
    };

    this.mutations.push(
      TransferProjectMutation.New(transferProjectInput, opts),
    );
  }

  onFeaturedProjectSuccess() {
    if (this.isFeatured === true) {
      Alert.info('The project will now appear as featured');
    } else {
      Alert.info('The project has been removed from the featured section');
    }
  }

  onFeaturedProjectError() {
    Alert.error("Couldn't change the featured attribute of the project!!");
  }

  onToggleProjectVisibilitySuccess() {
    if (this.isPrivate === true) {
      Alert.info('The project visibility has been set to Private');
    } else {
      Alert.info('The project now appears as Public');
    }
  }

  onToggleProjectVisibilityError() {
    Alert.error(`Error setting the project visibility to ${this.isPrivate}`);
  }

  onTransferProjectSuccess() {
    Alert.info(`Success transfering the project to ${this.targetSpace}`);
  }

  onTransferProjectError() {
    Alert.error(`Error transfering the project`);
  }

  onUserError(errors) {
    // For each returned error, show an alert with the code
    if (errors.length > 0) {
      for (let i = 0; i < errors.length; i += 1) {
        Alert.error(`Error doing the operation. Code: ${errors[i].code}`);
      }
    }
  }
}
